import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import api from "../Utils/api";
import Spinner from "../Components/Spinner";
import { useSelector } from "react-redux";

export default function CompanyManage() {
  const [isLoading, setIsLoading] = useState(false);
  const [company, setCompany] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    address: "",
    city: "",
    country: "",
    postCode: "",
    phone: "",
    email: "",
  });

  const user = useSelector((state) => state.user.user);

  useEffect(() => {
    if (user && user.deputyAccessLevel === "company_manager" && user.companyID) {
      getCompany();
    }
  }, [user]);

  const getCompany = async () => {
    try {
      setIsLoading(true);
      const response = await api.get(`/company/${user.companyID}`);
      setIsLoading(false);
      if (response.data.success) {
        setCompany(response.data.company);
        setFormData({
          name: response.data.company.name || "",
          description: response.data.company.description || "",
          address: response.data.company.address || "",
          city: response.data.company.city || "",
          country: response.data.company.country || "",
          postCode: response.data.company.postCode || "",
          phone: response.data.company.phone || "",
          email: response.data.company.email || "",
        });
      } else {
        toast.error("Failed to fetch company");
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch company");
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEdit = () => {
    if (company) {
      setFormData({
        name: company.name || "",
        description: company.description || "",
        address: company.address || "",
        city: company.city || "",
        country: company.country || "",
        postCode: company.postCode || "",
        phone: company.phone || "",
        email: company.email || "",
      });
      setShowEditModal(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error("Company name is required");
      return;
    }

    try {
      setIsLoading(true);
      const response = await api.post("/company/update", {
        id: company._id,
        updatedData: formData,
      });
      setIsLoading(false);
      if (response.data.success) {
        toast.success("Company updated successfully");
        getCompany();
        setShowEditModal(false);
      } else {
        toast.error(response.data.message || "Failed to update company");
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to update company");
      setIsLoading(false);
    }
  };

  // Only show for company managers
  if (!user || user.deputyAccessLevel !== "company_manager") {
    return (
      <div className="px-6 mt-20 mx-10 pb-20">
        <div className="text-center text-gray-500">
          You don't have permission to access this page.
        </div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="px-6 mt-20 mx-10 pb-20">
        {isLoading && <Spinner />}
        <div className="text-center text-gray-500">
          {isLoading ? "Loading company..." : "Company not found"}
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 mt-20 mx-10 pb-20">
      {isLoading && <Spinner />}

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-600">Company</h1>
        <button
          onClick={handleEdit}
          className="bg-gray-700 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-600 transition"
        >
          <i className="fa fa-edit mr-2"></i>
          Edit Company
        </button>
      </div>

      {/* Company Details Card */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Company Name
            </label>
            <p className="text-gray-900 font-semibold">{company.name}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <span
              className={`px-2 py-1 rounded text-xs font-semibold inline-block ${
                company.archive
                  ? "bg-gray-200 text-gray-700"
                  : "bg-green-100 text-green-700"
              }`}
            >
              {company.archive ? "Inactive" : "Active"}
            </span>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <p className="text-gray-600">{company.description || "-"}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <p className="text-gray-600">{company.email || "-"}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Address
            </label>
            <p className="text-gray-600">{company.address || "-"}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              City
            </label>
            <p className="text-gray-600">{company.city || "-"}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Country
            </label>
            <p className="text-gray-600">{company.country || "-"}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Post Code
            </label>
            <p className="text-gray-600">{company.postCode || "-"}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone
            </label>
            <p className="text-gray-600">{company.phone || "-"}</p>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-gray-700">Edit Company</h2>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="fa fa-times"></i>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                  rows="3"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Address
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    City
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Country
                  </label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Post Code
                  </label>
                  <input
                    type="text"
                    name="postCode"
                    value={formData.postCode}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone
                  </label>
                  <input
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                  />
                </div>
              </div>

              <div className="flex gap-3 justify-end mt-6">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600 transition"
                >
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

